import * as Sentry from "@sentry/react";
import { omit, path } from "ramda";

import { createLocalAxios } from "../../lib/api.js";
import apiMessages from "../../lib/apiMessages.js";

import {
  addContentItems,
  loadingBroadcasts,
  loadingBroadcastsDone,
  loadingContentItems,
  loadingContentItemsDone,
  loadingFolders,
  loadingFoldersDone,
  setBroadcasts,
  setContentItems,
  setFolders,
} from "./slice.js";

// Folders
export const fetchFolders = (params) => async (dispatch, getState) => {
  const { page, pageSize } = params || {};

  dispatch(loadingFolders());

  const localAxios = createLocalAxios(getState);
  const url =
    page && pageSize
      ? `arrivideo/folders?page=${page}&page_size=${pageSize}`
      : "arrivideo/folders";

  let response;
  try {
    response = await localAxios.get(url);
  } catch (error) {
    console.error("Error while fetching the folders", error);
    throw error;
  } finally {
    await dispatch(loadingFoldersDone());
  }

  await dispatch(setFolders(response.data.folders));

  return response.data.pages;
};

export const saveFolder = (values, folderId) => async (dispatch, getState) => {
  const localAxios = createLocalAxios(getState);
  const body = omit(["id", "createdAt", "updatedAt"], values);
  const url = folderId ? `arrivideo/folder/${folderId}` : "arrivideo/folder";

  let response;
  try {
    if (folderId) {
      response = await localAxios.patch(url, body);
    } else {
      response = await localAxios.post(url, body);
    }
  } catch (error) {
    console.error("Error while saving the folder:", error);
    Sentry.captureException(
      new Error(`Something went wrong: saveFolder post/patch-> ${error}`),
    );

    throw error;
  }

  if (response.data.message !== apiMessages.MSG_SUCCESS) {
    throw new Error(response.data.message);
  }

  return response;
};

export const removeFolder = (id) => async (dispatch, getState) => {
  const localAxios = createLocalAxios(getState);
  const url = `arrivideo/folder/${id}`;

  try {
    await localAxios.delete(url);
  } catch (error) {
    console.error(`Error deleting folder: ${error}`);
    Sentry.captureException(
      new Error(`Something went wrong: removeSoftwarePackage -> ${error}`),
    );
  }
};

export const fetchFolderById = (id) => async (dispatch, getState) => {
  const localAxios = createLocalAxios(getState);
  const url = `arrivideo/folder/${id}`;

  let response;
  try {
    response = await localAxios.get(url);
  } catch (error) {
    console.error("Error while fetching the folder by id:", error);
    new Error(`Something went wrong: fetchFolderById get -> ${error}`);
  }

  dispatch(setFolders([response.data]));
  return response.data;
};

// Broadcasts
export const fetchBroadcasts = (params) => async (dispatch, getState) => {
  const { page, pageSize, searchTitle, searchFolder } = params || {};

  dispatch(loadingBroadcasts());

  const localAxios = createLocalAxios(getState);
  let url = `arrivideo/broadcast?page=${page}&page_size=${pageSize}`;

  if (searchTitle || searchFolder) {
    const urlRoot = `arrivideo/broadcast?page=1&page_size=${pageSize}`;

    if (searchTitle && !searchFolder) {
      url = `${urlRoot}&filter[title]=${searchTitle}`;
    }

    if (searchFolder && !searchTitle) {
      url = `${urlRoot}&filter[arriVideoFolderId]=${searchFolder}`;
    }

    if (searchTitle && searchFolder) {
      url = `${urlRoot}&filter[arriVideoFolderId]=${searchFolder}&filter[title]=${searchTitle}`;
    }
  }

  let response;
  try {
    response = await localAxios.get(url);
  } catch (error) {
    console.error("Error while fetching the broadcasts", error);
    throw error;
  } finally {
    await dispatch(loadingBroadcastsDone());
  }

  await dispatch(setBroadcasts(response.data.broadcasts));

  return response.data.pages;
};

export const fetchBroadcastById = (id) => async (dispatch, getState) => {
  const localAxios = createLocalAxios(getState);
  const url = `arrivideo/broadcast/${id}`;

  let response;
  try {
    response = await localAxios.get(url);
  } catch (error) {
    console.error("Error while fetching the broadcast by id:", error);
    new Error(`Something went wrong: fetchBroadcastById get -> ${error}`);
  }

  dispatch(setBroadcasts([response.data.data]));
  return response.data.data;
};

export const removeBroadcast = (id) => async (dispatch, getState) => {
  const localAxios = createLocalAxios(getState);
  const url = `arrivideo/broadcast/${id}`;

  try {
    await localAxios.delete(url);
  } catch (error) {
    console.error(`Error deleting broadcast: ${error}`);
    Sentry.captureException(
      new Error(`Something went wrong: removeBroadcast -> ${error}`),
    );
  }
};

export const saveBroadcast =
  (values, broadcastId) => async (dispatch, getState) => {
    const localAxios = createLocalAxios(getState);
    const body = {
      ...omit(["audiences", "content", "createdAt", "id", "updatedAt"], values),
      audiences: (values.audiences || []).map((audience) => ({
        id: path(["value", "id"], audience),
        resourceType: path(["value", "resourceType"], audience),
      })),
      arriVideoFolderId: values.folder.id === "-1" ? null : values.folder.id,
      // "Geen actie" sets actionType to "", which the API's ENUM column rejects
      actionType: values.actionType || null,
      actionContent: values.actionType ? values.actionContent : null,
    };
    const url = broadcastId
      ? `arrivideo/broadcast/${broadcastId}`
      : "arrivideo/broadcast";

    let response;
    try {
      if (broadcastId) {
        response = await localAxios.patch(url, body);
      } else {
        response = await localAxios.post(url, body);
      }
    } catch (error) {
      console.error("Error while saving the broadcast:", error);
      Sentry.captureException(
        new Error(
          `Something went wrong: saveBroadcast post/patch-> ${error} (${path(["response", "data", "message"], error)})`,
        ),
      );
      throw error;
    }

    if (response.data.message !== apiMessages.MSG_SUCCESS) {
      throw new Error(response.data.message);
    }

    return response;
  };

// Content Items
export const fetchContentItemsByIds = (ids) => async (dispatch, getState) => {
  const localAxios = createLocalAxios(getState);
  const url = `arrivideo/me/content?ids=${ids}`;

  dispatch(loadingContentItems());
  let response;
  try {
    response = await localAxios.get(url);
  } catch (error) {
    console.error("Error while fetching the content items by ids:", error);
    new Error(`Something went wrong: fetchContentItemsByIds get -> ${error}`);
  } finally {
    dispatch(loadingContentItemsDone());
  }

  dispatch(addContentItems(response.data.content));
  return response.data;
};

export const fetchContentItems = (params) => async (dispatch, getState) => {
  const { page, pageSize, searchTitle } = params || {};

  dispatch(loadingContentItems());

  const localAxios = createLocalAxios(getState);
  let url = `arrivideo/content?page=${page}&page_size=${pageSize}`;

  if (searchTitle) {
    url += `&filter[title]=${searchTitle}`;
  }

  let response;
  try {
    response = await localAxios.get(url);
  } catch (error) {
    console.error("Error while fetching the content items", error);
    throw error;
  } finally {
    await dispatch(loadingContentItemsDone());
  }

  await dispatch(setContentItems(response.data.contentItems));

  return response.data.pages;
};

export const fetchContentItemById = (id) => async (dispatch, getState) => {
  const localAxios = createLocalAxios(getState);
  const url = `arrivideo/content/${id}`;

  dispatch(loadingContentItems());
  let response;
  try {
    response = await localAxios.get(url);
  } catch (error) {
    console.error("Error while fetching the content item by id:", error);
    new Error(`Something went wrong: fetchContentItemById get -> ${error}`);
  } finally {
    dispatch(loadingContentItemsDone());
  }

  dispatch(addContentItems([response.data.data]));
  return response.data.data;
};

export const saveContentItem =
  (values, contentId) => async (dispatch, getState) => {
    const localAxios = createLocalAxios(getState);
    const body = omit(["id", "createdAt", "updatedAt"], values);
    const url = contentId
      ? `arrivideo/content/${contentId}`
      : "arrivideo/content";

    let response;
    try {
      if (contentId) {
        response = await localAxios.patch(url, body);
      } else {
        response = await localAxios.post(url, body);
      }
    } catch (error) {
      console.error("Error while saving the content item:", error);
      Sentry.captureException(
        new Error(
          `Something went wrong: saveContentItem post/patch-> ${error}`,
        ),
      );
      throw error;
    }

    if (response.data.message !== apiMessages.MSG_SUCCESS) {
      throw new Error(response.data.message);
    }

    return response;
  };

export const removeContentItem = (id) => async (dispatch, getState) => {
  const localAxios = createLocalAxios(getState);
  const url = `arrivideo/content/${id}`;

  try {
    await localAxios.delete(url);
  } catch (error) {
    console.error(`Error deleting content item: ${error}`);
    Sentry.captureException(
      new Error(`Something went wrong: removeContentItem -> ${error}`),
    );
  }
};

export const fetchContentItemsFiltered =
  (params) => async (dispatch, getState) => {
    const { searchTitle } = params || {};

    const localAxios = createLocalAxios(getState);
    const url = `arrivideo/content?filter[title]=${searchTitle}`;

    let response;
    try {
      response = await localAxios.get(url);
    } catch (error) {
      console.error("Error while fetching the content items by title", error);
      throw error;
    }

    return response.data.contentItems;
  };
